import React, {
  Component,
  type FunctionComponent,
  type PropsWithChildren,
  type DragEvent as ReactDragEvent,
  type ReactNode,
} from 'react';
import type { ConnectedProps } from 'react-redux';

import _noop from 'lodash-es/noop';

import { isFirefox } from '@iconik-io/core/src/browser/is_firefox';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import type { DragName } from '../../ducks/dragged_items/constants';
import type { DragAndDropHSide, DragAndDropVSide } from '../../ui/table/types';
import { ui } from '../../utils/helpers';
import { eventElement, findAncestorId } from '../search_items/utils';
import connector from './connector';

const fakeDragImage = document.createElement('div');
fakeDragImage.style.width = '1px';
fakeDragImage.style.height = '1px';
fakeDragImage.style.position = 'fixed';
fakeDragImage.style.bottom = '0';
fakeDragImage.style.left = '0';
document.body.appendChild(fakeDragImage);

const userAgent = window.navigator.userAgent;
const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');

const DRAG_TRACK_EVENT = isSafari || isFirefox() ? 'dragover' : 'drag';

const OVER_PART_THRESHOLD = 3;

export type DragImageProps = PropsWithChildren<{
  items?: SearchItemEntity[];
  show: boolean;
  element: HTMLDivElement;
  altKey?: boolean;
  ctrlKey?: boolean;
  dropTargetId?: string;
  dropTargetElement?: SearchItemEntity;
  copyOnly?: boolean;
  withHint?: boolean;
}>;

export type DraggableItemsModifierKeys = {
  altKey: boolean;
};

export type DropParameters = {
  overPartH: DragAndDropHSide | undefined;
  overPartV: DragAndDropVSide | undefined;
};

export type DragOverParameters = DropParameters & {
  targetId: string | undefined;
};

export type ModifierKeysUpdatedParams = { altKey: boolean };

type Props = ConnectedProps<typeof connector> &
  PropsWithChildren<{
    disabled?: boolean;
    name: DragName;
    selectedItems: SearchItemEntity[];
    getTransferData?: () => string | undefined;
    setListDropTargetId: (id: string | undefined) => void;
    copyOnly: boolean;
    /**
     * An optional array of drop target ids.
     * If undefined, all items are allowed as drop targets.
     */
    allDropTargetIds?: string[];
    className?: string;
    dragImageComponent: FunctionComponent<DragImageProps>;
    onDragEnd?: () => void;
    onDragStart?: (items: SearchItemEntity[]) => void;
    onDrop?: (
      draggedItems: SearchItemEntity[],
      dropTargetId: string | null,
      modifierKeys: DraggableItemsModifierKeys,
      dropParameters: DropParameters,
    ) => void;
    onModifierKeysUpdated?: (modifierKeys: ModifierKeysUpdatedParams) => void;

    /**
     * Should be true when DraggableItems needs to be compatible with react-dnd.
     */
    propagateDropEvent?: boolean;

    onDragOver?: (parameters: DragOverParameters) => void;
  }>;

type State = {
  dragging: boolean;
  altKey?: boolean;
  ctrlKey?: boolean;
};

class DraggableItems extends Component<Props, State> {
  static defaultProps = {
    disabled: false,
    copyOnly: false,
    setListDropTargetId: _noop,
    selectedItems: [],
  };

  state = {
    dragging: false,
    altKey: false,
  };

  #dragEnterCounter = 0;

  #dragImage = document.createElement('div');

  #dragRef = React.createRef<HTMLDivElement>();

  dragClientX: number | undefined;

  dragClientY: number | undefined;

  animationFrameId: number | undefined;

  overPartH: DragAndDropHSide | undefined;

  overPartV: DragAndDropVSide | undefined;

  #allDropTargetIdsSet = this.props.allDropTargetIds
    ? new Set(this.props.allDropTargetIds)
    : undefined;

  constructor(props: Props) {
    super(props);

    this.#dragImage.style.position = 'fixed';
    this.#dragImage.style.top = '0';
    this.#dragImage.style.left = '0';
    this.#dragImage.style.transform = 'translate(-500px, -500px)';
    this.#dragImage.style.zIndex = 'var(--z-index-drag)';
    this.#dragImage.style.pointerEvents = 'none';
  }

  componentDidMount(): void {
    document.body.addEventListener(DRAG_TRACK_EVENT, this.handleDragPosition);

    // Track dragover to allow drop event happen
    // https://stackoverflow.com/questions/8414154/html5-drop-event-doesnt-work-unless-dragover-is-handled
    const dragElement = this.#dragRef.current;
    if (dragElement && DRAG_TRACK_EVENT !== 'dragover') {
      dragElement.addEventListener('dragover', this.handleDragOver);
    }

    document.body.appendChild(this.#dragImage);
  }

  componentWillUnmount(): void {
    const dragElement = this.#dragRef.current;

    document.body.removeEventListener(
      DRAG_TRACK_EVENT,
      this.handleDragPosition,
    );

    if (dragElement && DRAG_TRACK_EVENT !== 'dragover') {
      dragElement.removeEventListener('dragover', this.handleDragOver);
    }

    document.body.removeChild(this.#dragImage);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.allDropTargetIds !== this.props.allDropTargetIds) {
      this.#allDropTargetIdsSet = this.props.allDropTargetIds
        ? new Set(this.props.allDropTargetIds)
        : undefined;
    }
  }

  handleDragPosition = (event: DragEvent): void => {
    const valuesChanged =
      this.dragClientX !== event.clientX || this.dragClientY !== event.clientY;
    if (event.clientX && event.clientY && valuesChanged) {
      this.dragClientX = event.clientX;
      this.dragClientY = event.clientY;
    }

    let altKey = event.altKey;
    if (isSafari) {
      // Safari alt key is not updating in event object, only effectAllowed
      altKey = event?.dataTransfer?.effectAllowed === 'copy';
    }

    if (altKey !== this.state.altKey) {
      this.setState({
        altKey,
      });

      if (this.props.onModifierKeysUpdated) {
        this.props.onModifierKeysUpdated({
          altKey,
        });
      }
    }

    if (DRAG_TRACK_EVENT === 'dragover') {
      this.handleDragOver(event);
    }
  };

  loopRenderFunction = (): void => {
    this.#dragImage.style.transform = `translate(${this.dragClientX}px, ${this.dragClientY}px)`;

    this.animationFrameId = requestAnimationFrame(this.loopRenderFunction);
  };

  handleDragEnter = (event: ReactDragEvent): void => {
    const element = eventElement(event.nativeEvent.target);
    const id = findAncestorId(element);

    if (!id) {
      return;
    }
    const { name } = this.props;

    if (!this.#allDropTargetIdsSet || this.#allDropTargetIdsSet.has(id)) {
      this.#dragEnterCounter += 1;
      this.props.setDropTargetId({ name, id });
      this.props.setListDropTargetId(id);
    }
  };

  handleDragLeave = (event: ReactDragEvent): void => {
    const element = eventElement(event.nativeEvent.target);
    const id = findAncestorId(element);
    if (!id) {
      return;
    }

    const { name } = this.props;

    if (!this.#allDropTargetIdsSet || this.#allDropTargetIdsSet.has(id)) {
      this.#dragEnterCounter -= 1;
    }
    if (this.#dragEnterCounter === 0) {
      this.props.setDropTargetId({ name, id: undefined });
      this.props.setListDropTargetId(undefined);
    }
  };

  handleDragStart = (event: ReactDragEvent): void => {
    const { selectedItems, name, disabled } = this.props;
    if (disabled) {
      return;
    }

    event.dataTransfer.setData(
      'application/node type',
      selectedItems.toString(),
    );

    const element = eventElement(event.nativeEvent.target);
    const id = findAncestorId(element);
    if (!id) {
      return;
    }

    event.stopPropagation();

    event.dataTransfer.effectAllowed = 'all';
    event.dataTransfer.setDragImage(fakeDragImage, 125, 70);

    if (this.props.getTransferData) {
      const data = this.props.getTransferData();
      if (data) {
        event.dataTransfer.setData('text', data);
      }
    }

    this.props.onDragStart?.(selectedItems);
    this.props.setDraggedItems({ name, draggedItems: selectedItems });

    this.setState(
      {
        dragging: true,
      },
      () => {
        this.loopRenderFunction();
      },
    );
  };

  updateOverPartProperty = (event: DragEvent): void => {
    const overlayElement = document.querySelector(
      `[data-drop-overlay-id="${this.props.dropTargetId}"]`,
    );
    if (!overlayElement) {
      return;
    }

    const rect = overlayElement.getBoundingClientRect();

    const halfWidth = rect.width / 2;
    const overLeftPart =
      event.clientX < rect.right - halfWidth &&
      event.clientX >= rect.left - OVER_PART_THRESHOLD &&
      event.clientY <= rect.bottom &&
      event.clientY >= rect.top;
    const overRightPart =
      event.clientX <= rect.right + OVER_PART_THRESHOLD &&
      event.clientX >= rect.left + halfWidth &&
      event.clientY <= rect.bottom &&
      event.clientY >= rect.top;
    if (overLeftPart) {
      this.overPartH = 'left';
    } else if (overRightPart) {
      this.overPartH = 'right';
    } else {
      this.overPartH = undefined;
    }

    const halfHeight = rect.height / 2;
    const overTopPart =
      event.clientY < rect.bottom - halfHeight &&
      event.clientY >= rect.top - OVER_PART_THRESHOLD &&
      event.clientX <= rect.right &&
      event.clientX >= rect.left;
    const overBottomPart =
      event.clientY <= rect.bottom + OVER_PART_THRESHOLD &&
      event.clientY >= rect.top + halfHeight &&
      event.clientX <= rect.right &&
      event.clientX >= rect.left;

    if (overTopPart) {
      this.overPartV = 'top';
    } else if (overBottomPart) {
      this.overPartV = 'bottom';
    } else {
      this.overPartV = undefined;
    }
  };

  handleDragOver = (event: DragEvent): void => {
    if (this.props.dropTargetId) {
      this.updateOverPartProperty(event);
    } else {
      this.overPartH = undefined;
      this.overPartV = undefined;
    }

    this.props.onDragOver?.({
      overPartH: this.overPartH,
      overPartV: this.overPartV,
      targetId: this.props.dropTargetId,
    });

    if (this.props.draggedItems.length > 0 && !this.props.disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  handleDragEnd = (): void => {
    const { name } = this.props;

    this.#dragEnterCounter = 0;
    this.props.setDraggedItems({ name, draggedItems: [] });
    this.props.setDropTargetId({ name, id: undefined });
    this.props.setListDropTargetId(undefined);

    if (this.props.onModifierKeysUpdated) {
      this.props.onModifierKeysUpdated({
        altKey: false,
      });
    }

    this.setState(
      {
        dragging: false,
        altKey: false,
      },
      () => {
        this.dragClientX = -500;
        this.dragClientY = -500;
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
        }
      },
    );

    if (this.props.onDragEnd) {
      this.props.onDragEnd();
    }
  };

  handleDrop = (event: ReactDragEvent): void => {
    // Prevents "Invariant Violation: Cannot call hover while not dragging." react-dnd errors
    // https://sentry.io/organizations/cantemo-ab/issues/1770000107/
    if (!this.props.propagateDropEvent) {
      event.stopPropagation();
    }

    const element = eventElement(event.nativeEvent.target);
    const dropTargetId = findAncestorId(element);
    if (this.props.onDrop) {
      this.props.onDrop(
        this.props.draggedItems,
        dropTargetId,
        {
          altKey: this.state.altKey,
        },
        {
          overPartH: this.overPartH,
          overPartV: this.overPartV,
        },
      );
    }

    const { name } = this.props;
    this.props.setDropTargetId({ name, id: undefined });
    this.props.setListDropTargetId(undefined);
  };

  render(): ReactNode {
    const {
      children,
      dropTargetId,
      dragImageComponent: DragImage,
      draggedItems,
      copyOnly,
      dropTargetElement,
      className,
      editSortMode,
    } = this.props;
    const { dragging, altKey } = this.state;

    return (
      <>
        <div
          ref={this.#dragRef}
          className={className}
          onDragStart={this.handleDragStart}
          onDragEnd={this.handleDragEnd}
          onDrop={this.handleDrop}
          onDragEnter={this.handleDragEnter}
          onDragLeave={this.handleDragLeave}
          {...ui('draggable-wrapper')}
        >
          {children}
        </div>
        <DragImage
          items={draggedItems}
          show={dragging}
          element={this.#dragImage}
          altKey={altKey}
          dropTargetId={dropTargetId}
          dropTargetElement={dropTargetElement}
          copyOnly={copyOnly}
          withHint={!editSortMode}
        />
      </>
    );
  }
}

export { DraggableItems };
export default connector(DraggableItems);
